import type { ReactElement } from 'react';

/* The Metadata Copilot's mark: a faceless core inside two orbit rings.
 *
 * **Abstract on purpose**, as the design draws it: no face, no eyes, no robot — a node with two
 * orbits, which says "something is reading the catalogue" without claiming a personality. Every
 * state is a CSS keyframe on the core or the rings, so the animation costs no script and stops
 * entirely under `prefers-reduced-motion` (see `.lq-mascot` in `components.css`).
 *
 * The five states are the design's: `idle` breathes slowly, `thinking` spins the rings while a
 * question is in flight, `insight` pulses a ring and wears a dot when something is waiting for a
 * person, `success` settles after an answer, and `error` turns the core warm and stops it. A state
 * is information only alongside words — every consumer also says what happened in text.
 *
 * The size is a custom property rather than a prop-driven inline length, so the rings and core scale
 * from one number and no component writes a raw pixel value into a style.
 */

export type MascotState = 'idle' | 'thinking' | 'insight' | 'success' | 'error';

export type MascotSize = 'sm' | 'md' | 'lg' | 'xl';

export function CopilotMascot({
  state = 'idle',
  size = 'md',
  label,
}: {
  readonly state?: MascotState;
  readonly size?: MascotSize;
  /** An accessible name when the mark stands alone; decorative otherwise. */
  readonly label?: string;
}): ReactElement {
  const named = label !== undefined && label !== '';
  return (
    <span
      className={`lq-mascot lq-mascot--${size}`}
      data-state={state}
      role={named ? 'img' : undefined}
      aria-label={named ? label : undefined}
      aria-hidden={named ? undefined : true}
    >
      <span className="lq-mascot__ring lq-mascot__ring--x" />
      <span className="lq-mascot__ring lq-mascot__ring--y" />
      <span className="lq-mascot__core" />
      {state === 'insight' ? <span className="lq-mascot__dot" /> : null}
    </span>
  );
}
